import React, { memo, useCallback, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';

import Table from '@components/common/Table';

import useMutateConsistencyCheckBranchExport from '@hooks/consistencyCheck/useMutateConsistencyCheckBranchExport';
import useConsistencyCheckDetails from '@hooks/consistencyCheckDetails/useConsistancyCheckDetails';

import { generateFilters, getDynamicColumns } from '@utils/cmModule';
import { dateFormattingUTCDayMonthYear } from '@utils/date';

import { ICMBranch, IManagedObjects } from '@interfaces/cmModule/cmModule';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';

import { defaultMeta } from '@enums/common';

import { filterWidth } from './constants';

import { getColumns } from './columns';

import styles from './ConsistencyCheckDetails.module.scss';

interface IConsistencyCheckDetailsProps {
	selectedTableBranch: ICMBranch;
	isMavenirVendor: boolean;
}

const ConsistencyCheckDetails = ({ selectedTableBranch, isMavenirVendor }: IConsistencyCheckDetailsProps) => {
	const [selectedDelta, setSelectedDelta] = useState<TSelectOption<string> | null>(null);
	const [selectedSiteId, setSelectedSiteId] = useState<TSelectOption<string> | null>(null);
	const [selectedConfigId, setSelectedConfigId] = useState<TSelectOption<string> | null>(null);
	const [selectedGnbId, setSelectedGnbId] = useState<TSelectOption<string> | null>(null);
	const [selectedParamName, setSelectedParamName] = useState<TSelectOption<string> | null>(null);
	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState('');
	const [selectedGeneratedFilters, setSelectedGeneratedFilters] = useState<Record<string, TSelectOption>>({});

	const comparisonId = selectedTableBranch?.id;

	const managedObjects = useMemo(() => {
		return Object.entries(selectedGeneratedFilters).reduce<IManagedObjects[]>((acc, [key, option]) => {
			const moItem = option?.value ? { moName: key, moId: option.value.toString() } : null;

			return moItem
				? [...acc, moItem]
				: acc;
		}, []);
	}, [selectedGeneratedFilters]);

	const {
		details,
		moNames,
		isLoadingDetails,
		countOfRecords,
		siteIdsOptions,
		gnbIdsOptions,
		configIdsOptions,
		parametersOptions,
		deltasOptions,
		dataSourceCreatedAt,
		detailsMoFilters,
	} = useConsistencyCheckDetails({
		comparisonId,
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		delta: selectedDelta?.value,
		paramName: selectedParamName?.value,
		siteId: selectedSiteId?.value,
		gnbId: selectedGnbId?.value,
		configId: selectedConfigId?.value,
		searchValue,
		managedObjects,
		isMavenirVendor,
	});

	const { handleConsistencyCheckBranchExport } = useMutateConsistencyCheckBranchExport();

	const handleExport = useCallback(() => {
		if (!comparisonId) return;

		const dto = {
			comparisonId,
			ids: [],
			isAll: true,
			filter: undefined,
		};

		return handleConsistencyCheckBranchExport(dto);
	}, [
		handleConsistencyCheckBranchExport,
		comparisonId,
	]);

	const formattedDetailsDate = dataSourceCreatedAt ? dateFormattingUTCDayMonthYear(dataSourceCreatedAt) : '';

	const search: Array<ITableSearch> = useMemo(() => [{
		initialValue: '',
		name: 'name',
		handler: (searchValue: string) => setSearchValue(searchValue),
		tooltipText: 'Search by parameter and site ID',
	}], [setSearchValue]);

	const moFilters = generateFilters(
		detailsMoFilters,
		selectedGeneratedFilters,
		setSelectedGeneratedFilters,
	);

	const vendorFilters = useMemo(() => {
		return isMavenirVendor
			? [
				{
					name: 'configId',
					placeholder: 'CNF ID',
					option: selectedConfigId,
					options: configIdsOptions,
					minWidth: filterWidth,
					setState: setSelectedConfigId,
					defaultValue: 'configId',
				},
				{
					name: 'gnbId',
					placeholder: 'NODE ID',
					option: selectedGnbId,
					options: gnbIdsOptions,
					minWidth: filterWidth,
					setState: setSelectedGnbId,
					defaultValue: 'gnbId',
				},
				...moFilters,
			] : [{
				name: 'siteId',
				placeholder: 'Site ID',
				option: selectedSiteId,
				options: siteIdsOptions,
				minWidth: filterWidth,
				setState: setSelectedSiteId,
				defaultValue: 'siteId',
			}];
	}, [
		isMavenirVendor,
		moFilters,
		configIdsOptions,
		gnbIdsOptions,
		selectedGnbId,
		selectedConfigId,
		selectedSiteId,
		siteIdsOptions,
	]);

	const filters: Array<TTableFilter> = useMemo(() => [
		{
			name: 'paramName',
			placeholder: 'Parameter',
			option: selectedParamName,
			options: parametersOptions,
			minWidth: filterWidth,
			setState: setSelectedParamName,
			defaultValue: 'paramName',
		},
		{
			name: 'delta',
			placeholder: 'Delta',
			option: selectedDelta,
			options: deltasOptions,
			minWidth: filterWidth,
			setState: setSelectedDelta,
			defaultValue: 'delta',
		},
		...vendorFilters,
	], [
		selectedParamName,
		selectedDelta,
		parametersOptions,
		deltasOptions,
		vendorFilters,
	]);

	const columns = useMemo(() => {
		return getColumns({ date: formattedDetailsDate, isMavenirVendor });
	}, [formattedDetailsDate, isMavenirVendor]);

	const dynamicColumns = useMemo(() => getDynamicColumns(moNames), [moNames]);

	const branchColumns: ColumnDef<any>[] = useMemo(() => {
		return dynamicColumns ? [...columns, ...dynamicColumns] : columns;
	}, [columns, dynamicColumns]);

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPage(defaultMeta.page);
		setPageSize(pageSize);
	}, []);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [countOfRecords, page, pageSize, pageHandler, pageSizeHandler]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	return (
		<div className={styles.tableWrapper}>
			<Table
				columns={branchColumns}
				data={details}
				exportTooltipText='Export Branch to Excel'
				filters={filters}
				handleCustomExport={handleExport}
				isExport
				isLoading={isLoadingDetails}
				isPinColumns={isMavenirVendor}
				isWithoutActions
				isWithoutDeleteAction
				pagination={pagination}
				search={search}
				searchThreshold={0}
				sort={sort}
				stickyLeftColumnIndexes={[0, 1, 2, 3]}
			/>
		</div>
	);
};

export default memo(ConsistencyCheckDetails);
